import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useRoute, useNavigation } from '@react-navigation/native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';
import ACHDetailsForm from './components/ACHDetailsForm';
import CreditCardDetailsForm from './components/CreditCardDetailsForm';
import { PaymentMethodSelector } from '@/components/residents/payments/PaymentMethodSelector';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { useLazyGetFeeStructureQuery } from '@/store/api';
import { transformFeeStructureDollarSign } from '@/utils/residents/paymentUtils';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';

type Params = {
  leaseId: number;
  residentId?: number;
  returnTo?: string;
  paymentType?: 'ach' | 'card';
  successPage?: string;
  managePaymentPage?: string;
};

export enum PAYMENT_METHOD_TYPE {
  NONE = 'none',
  ACH = 'ach',
  CARD = 'card',
}

export const AddOrChangePaymentMethodModal: React.FC = () => {
  const { colors, semantic } = useThemeColors();
  const styles = usePaymentMethodStyles();
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const { leaseId, residentId, returnTo, paymentType, successPage, managePaymentPage } = (route.params || {}) as Params;

  const [selectedType, setSelectedType] = useState<PAYMENT_METHOD_TYPE>(
    paymentType === 'ach' ? PAYMENT_METHOD_TYPE.ACH :
    paymentType === 'card' ? PAYMENT_METHOD_TYPE.CARD :
    PAYMENT_METHOD_TYPE.NONE
  );

  // Get payment data to check if user has existing methods
  const paymentData = usePaymentData(residentId);
  const { paymentMethods, selectedPaymentMethod, updateSelectedPaymentMethod } = paymentData;
  const hasValidPaymentMethods = paymentMethods.length > 0;

  // Get fee structure for display
  const [getFeeStructure, { data: feeStructure }] = useLazyGetFeeStructureQuery();

  useEffect(() => {
    getFeeStructure(undefined, true);
  }, [getFeeStructure]);

  const fees = {
    creditCard: feeStructure?.creditCard || '',
    debitCard: feeStructure?.debitCard || '',
    bankAccount: feeStructure?.bankAccount || '',
  };

  const handleBack = () => {
    if (selectedType !== PAYMENT_METHOD_TYPE.NONE) {
      setSelectedType(PAYMENT_METHOD_TYPE.NONE);
    } else {
      navigation.goBack();
    }
  };

  const handleSuccess = () => {
    if (successPage) {
      navigation.replace(successPage, { leaseId });
    } else if (returnTo) {
      navigation.replace(returnTo, { leaseId });
    } else {
      navigation.goBack();
    }
  };

  const handleManagePaymentMethods = () => {
    // Replace current modal with manage wallet modal
    if (managePaymentPage) {
      navigation.replace(managePaymentPage, { leaseId, returnTo });
    } else {
      navigation.replace('ManageMyWallet', { leaseId, returnTo });
    }
  };

  const onClose = () => navigation.goBack();

  const modalTitle = hasValidPaymentMethods ? "Change your payment method" : "Add a payment method";

  const renderMethodSelection = () => (
    <View style={styles.content}>
      <Text style={styles.selectionTitle}>Please choose the type of payment you wish to use</Text>

      <View style={styles.methodOptions}>
        <PaymentMethodOption
          icon="CREDIT_CARD"
          title="Credit or Debit Card"
          subtitle="Add a credit or debit card"
          onPress={() => setSelectedType(PAYMENT_METHOD_TYPE.CARD)}
          iconColor={colors.text.primary}
          subtitleColor={colors.text.subtle}
          titleColor={colors.text.primary}
          arrowColor={colors.text.subtle}
          cardBg={colors.glass.buttonBg}
          cardBorder={colors.card.border}
        />

        <PaymentMethodOption
          icon="BANK"
          title="Bank Account (ACH)"
          subtitle="Add a checking or savings account"
          onPress={() => setSelectedType(PAYMENT_METHOD_TYPE.ACH)}
          iconColor={colors.text.primary}
          subtitleColor={colors.text.subtle}
          titleColor={colors.text.primary}
          arrowColor={colors.text.subtle}
          cardBg={colors.glass.buttonBg}
          cardBorder={colors.card.border}
        />
      </View>

      {hasValidPaymentMethods && (
        <View style={styles.existingMethodsSection}>
          <View style={styles.existingMethodsHeader}>
            <Text style={styles.existingMethodsTitle}>Saved payment methods</Text>
            <TouchableOpacity onPress={handleManagePaymentMethods}>
              <Text style={styles.manageLink}>Manage</Text>
            </TouchableOpacity>
          </View>

          <PaymentMethodSelector
            paymentMethods={paymentMethods}
            selectedMethod={selectedPaymentMethod}
            onSelectMethod={updateSelectedPaymentMethod}
            onManagePaymentMethods={handleManagePaymentMethods}
            isLoading={false}
            showChangeButton={false}
            maxHeight={240}
          />
        </View>
      )}

      {!hasValidPaymentMethods && (
        <View style={styles.feesSection}>
          <Text style={styles.feesTitle}>Convenience fees</Text>
          <View style={styles.feesTable}>
            <View style={styles.feeRow}>
              <Text style={styles.feeLabel}>Credit</Text>
              <Text style={styles.feeValue}>{transformFeeStructureDollarSign(fees.creditCard)}</Text>
            </View>
            <View style={styles.feeRow}>
              <Text style={styles.feeLabel}>Debit</Text>
              <Text style={styles.feeValue}>{transformFeeStructureDollarSign(fees.debitCard)}</Text>
            </View>
            <View style={styles.feeRow}>
              <Text style={styles.feeLabel}>ACH</Text>
              <Text style={styles.feeValue}>{transformFeeStructureDollarSign(fees.bankAccount)}</Text>
            </View>
          </View>
        </View>
      )}
    </View>
  );

  const renderFormContent = () => {
    switch (selectedType) {
      case PAYMENT_METHOD_TYPE.ACH:
        return (
          <ACHDetailsForm
            leaseId={leaseId}
            onSuccess={handleSuccess}
            onBack={handleBack}
            showMakeDefault={true}
            showInstructions={true}
          />
        );

      case PAYMENT_METHOD_TYPE.CARD:
        return (
          <CreditCardDetailsForm
            leaseId={leaseId}
            onSuccess={handleSuccess}
            onBack={handleBack}
            showMakeDefault={true}
            showInstructions={true}
          />
        );

      default:
        return renderMethodSelection();
    }
  };

  const getTitle = () => {
    switch (selectedType) {
      case PAYMENT_METHOD_TYPE.ACH: return 'ACH Details';
      case PAYMENT_METHOD_TYPE.CARD: return 'Credit or Debit Card Details';
      default: return modalTitle;
    }
  };

  const footerElement = selectedType !== PAYMENT_METHOD_TYPE.NONE ? undefined : (
    <View style={staticStyles.footerRow}>
      <ModalButton
        label={hasValidPaymentMethods ? "Cancel" : "Do it later"}
        variant="secondary"
        onPress={onClose}
      />
      <ModalButton
        label={hasValidPaymentMethods ? "Save" : "Add Now"}
        variant="primary"
        onPress={hasValidPaymentMethods ? handleSuccess : () => {}}
      />
    </View>
  );

  return (
    <UnifiedModal
      visible={true}
      variant="dialog"
      dialogSize="large"
      onClose={onClose}
      title={getTitle()}
      scrollable
      footer={footerElement}
    >
      {renderFormContent()}
    </UnifiedModal>
  );
};

interface PaymentMethodOptionProps {
  icon: string;
  title: string;
  subtitle: string;
  onPress: () => void;
  iconColor: string;
  subtitleColor: string;
  titleColor: string;
  arrowColor: string;
  cardBg: string;
  cardBorder: string;
}

const PaymentMethodOption: React.FC<PaymentMethodOptionProps> = ({
  icon,
  title,
  subtitle,
  onPress,
  iconColor,
  subtitleColor,
  titleColor,
  arrowColor,
  cardBg,
  cardBorder,
}) => {
  const IconComponent = (RESIDENTS_SVG_COMPONENTS as any)[icon];

  return (
    <TouchableOpacity
      style={[staticStyles.methodOption, { backgroundColor: cardBg, borderColor: cardBorder }]}
      onPress={onPress}
    >
      <View style={staticStyles.methodIcon}>
        {IconComponent && (
          <IconComponent
            width={32}
            height={32}
            color={iconColor}
          />
        )}
      </View>

      <View style={staticStyles.methodContent}>
        <Text style={[staticStyles.methodTitle, { color: titleColor }]}>{title}</Text>
        <Text style={[staticStyles.methodSubtitle, { color: subtitleColor }]}>{subtitle}</Text>
      </View>

      <View style={staticStyles.methodArrow}>
        {(() => {
          const ArrowIcon = (RESIDENTS_SVG_COMPONENTS as any).CHEVRON_RIGHT;
          return ArrowIcon ? (
            <ArrowIcon
              width={20}
              height={20}
              color={arrowColor}
            />
          ) : null;
        })()}
      </View>
    </TouchableOpacity>
  );
};

// Static styles that don't depend on theme
const staticStyles = StyleSheet.create({
  methodOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
  },
  methodIcon: {
    marginRight: 16,
  },
  methodContent: {
    flex: 1,
  },
  methodTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  methodSubtitle: {
    fontSize: 14,
  },
  methodArrow: {
    marginLeft: 12,
  },
  footerRow: {
    flexDirection: 'row',
    gap: 12,
  },
});

const usePaymentMethodStyles = () =>
  useThemedStyles(({ colors, semantic }) => ({
    content: {
      padding: 20,
    },
    selectionTitle: {
      fontSize: 16,
      fontWeight: '600' as const,
      color: colors.text.primary,
      marginBottom: 24,
    },
    methodOptions: {
      marginBottom: 32,
    },
    existingMethodsSection: {
      marginBottom: 32,
    },
    existingMethodsHeader: {
      flexDirection: 'row' as const,
      justifyContent: 'space-between' as const,
      alignItems: 'center' as const,
      marginBottom: 16,
    },
    existingMethodsTitle: {
      fontSize: 16,
      fontWeight: '600' as const,
      color: colors.text.primary,
    },
    manageLink: {
      fontSize: 16,
      fontWeight: '600' as const,
      color: semantic.interactive.primary,
    },
    feesSection: {
      marginBottom: 32,
    },
    feesTitle: {
      fontSize: 16,
      fontWeight: '600' as const,
      color: colors.text.primary,
      marginBottom: 16,
    },
    feesTable: {
      backgroundColor: colors.glass.buttonBg,
      borderRadius: 12,
      padding: 16,
    },
    feeRow: {
      flexDirection: 'row' as const,
      justifyContent: 'space-between' as const,
      alignItems: 'center' as const,
      paddingVertical: 12,
    },
    feeLabel: {
      fontSize: 14,
      color: colors.text.primary,
    },
    feeValue: {
      fontSize: 14,
      fontWeight: '600' as const,
      color: colors.accent.green,
    },
  }));

export default AddOrChangePaymentMethodModal;
